import { agentClient } from "../../lib/agentClient";
import { bearer } from "../../lib/accessToken";
import type {
  WorkSettingsDefinition,
  WorkSettingsSnapshot,
} from "../../../../shared/work-settings";

export interface SettingsScope {
  token: string;
  orgId: string;
  projectId: string;
  userId: string;
  threadId: string | null;
}

function scopeQuery(scope: SettingsScope): URLSearchParams {
  return new URLSearchParams({
    organization_id: scope.orgId,
    project_id: scope.projectId,
    user_id: scope.userId,
  });
}

async function readJson(path: string, scope: SettingsScope, signal: AbortSignal): Promise<Record<string, unknown>> {
  const response = await agentClient.fetch(path, { signal, headers: { Authorization: bearer(scope.token) } });
  const body = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    const error = body["error"] as { message?: string } | string | undefined;
    throw new Error(
      typeof error === "string" ? error : (error?.message ?? (body["message"] as string | undefined) ?? `HTTP ${response.status}`),
    );
  }
  return body;
}

const id = (value: string) => encodeURIComponent(value);

/**
 * Which workflow a saved conversation belongs to, from the agent's record of it
 * (`GET /workflows/threads/:threadId`): null for an ordinary chat. This is what
 * decides where the conversation's turns go, so it is read before the
 * conversation is drawn.
 */
export async function lookupThreadWorkflow(scope: SettingsScope & { threadId: string }, signal: AbortSignal): Promise<string | null> {
  const body = await readJson(`/workflows/threads/${id(scope.threadId)}?${scopeQuery(scope)}`, scope, signal);
  return typeof body["workflowId"] === "string" ? body["workflowId"] : null;
}

/**
 * The workflow's start form (`GET /workflows/:id/settings`) and, for a saved
 * conversation, the values it was started with
 * (`GET /workflows/:id/threads/:threadId/settings`).
 */
export async function readSettings(
  scope: SettingsScope,
  workflowId: string | null,
  signal: AbortSignal,
): Promise<{
  definitions: WorkSettingsDefinition[];
  snapshot: WorkSettingsSnapshot | null;
}> {
  if (!workflowId) return { definitions: [], snapshot: null };
  const query = scopeQuery(scope);
  const [definition, history] = await Promise.all([
    readJson(`/workflows/${id(workflowId)}/settings?${query}`, scope, signal),
    scope.threadId
      ? readJson(`/workflows/${id(workflowId)}/threads/${id(scope.threadId)}/settings?${query}`, scope, signal)
      : Promise.resolve<Record<string, unknown>>({}),
  ]);
  return {
    definitions: definition["definition"] ? [definition["definition"] as WorkSettingsDefinition] : [],
    snapshot: (history["snapshot"] as WorkSettingsSnapshot | null | undefined) ?? null,
  };
}
